// The Frontier's pieces, the camps' pieces, and what happens when they meet.
//
// This replaced `civ.band` - one token that had to be in the right hex for
// every verb the role owned, and that cost the town a worker at every dawn
// so that wanting a second one was self-harm. See docs/DESIGN.md, "the
// frontier, the network, and what is out there".
//
// ONE NUMBER DOES EVERYTHING. A unit's `strength` is its size, its dice and
// its hit points at once. A warband of 4 rolls four dice; knock two off it
// and it rolls two. Matt, 2026-09-20: "maybe it's just the strength count
// gets reduced, so they fight at lessened strength if some of the units are
// killed."
//
// Pure: no randomness lives here except where an rng is handed in, and
// nothing here knows about pixels.

import { BAND, UNIT_KINDS, RAIDER_KINDS, RAIDER_GROW, CAMP, VETERAN_AT, WARBAND_TIERS } from './constants';
import { hexDistanceBetween, canStand, isTown, key as hexKey } from './map';
import { stepCost } from './terrain';
import { modsOf } from './mods';
import { isRivalGround } from './rivals';
import { roadReach, onRoad } from './roads';
import { nextInt } from './rng/rng';

export const unitsOf = (civ) => civ?.units || [];
export const raidersOf = (civ) => civ?.raiders || [];
export const unitAt = (civ, q, r) => unitsOf(civ).find((u) => u.q === q && u.r === r) || null;
export const raiderAt = (civ, q, r) => raidersOf(civ).find((x) => x.q === q && x.r === r) || null;
export const unitById = (civ, id) => unitsOf(civ).find((u) => u.id === id) || null;

/** What a kind of unit is and what it may do. */
export const kindOf = (unit) => UNIT_KINDS[unit?.kind] || UNIT_KINDS.warband;

/** The city, and any chartered town: places units may stack. */
export const isMuster = (civ, q, r) => (q === 0 && r === 0)
  || (civ.villages || []).some((v) => v.chartered && !v.taken && v.q === q && v.r === r);
/** A town of ours another people holds (DESIGN.md 2026-09-28: villages are razed, towns are taken and can be won back). */
export const takenTownAt = (civ, q, r) => (civ.villages || []).find((v) => v.taken && v.q === q && v.r === r) || null;

/**
 * What it costs in ORDERS to step from where a unit is to (q, r).
 * A worker is one unit's turn, so this is priced in the same currency as
 * everything else the Frontier does. Scouts ignore rough ground.
 */
export const stepOrders = (civ, unit, q, r) => {
  if (!unit) return 0;
  if (kindOf(unit).ignoresRough) return 1;
  // Along the road: one order, whatever the ground (DESIGN.md "ROADS").
  if (onRoad(civ, unit.q, unit.r) && roadReach(civ, unit.q, unit.r).some(([a, b]) => a === q && b === r)) return 1;
  return stepCost(civ, q, r, modsOf(civ).marchRange);
};

/** Where this unit could step to, ignoring what it can afford. */
/** May this unit end a move on (q, r)? The rules every step shares. */
const mayStandFor = (civ, unit, q, r) => {
  if (!canStand(q, r, civ)) return false;
  // Your own pieces do not stack in the field - two piles of meeples on
  // one hex is unreadable - but they DO stack at a muster point, which
  // is a place with room in it rather than a patch of ground. Without
  // that, the unit standing at home blocked the city and the Stewards
  // could not train a second one.
  if (unitAt(civ, q, r) && unitAt(civ, q, r).id !== unit.id && !isMuster(civ, q, r)) return false;
  if ((civ.menaces || []).some((m) => m.q === q && m.r === r)) return false;
  if (raiderAt(civ, q, r)) return false;
  // Another people's ground is theirs. Raid it from beside it - except a
  // town of ours they hold, which a warband walks into to take back.
  if (isRivalGround(civ, q, r) && !takenTownAt(civ, q, r)) return false;
  return true;
};

/**
 * Where this unit could step to, ignoring what it can afford: its six
 * neighbours, and - standing on road - anywhere one order carries it along
 * the road (roads.js roadReach).
 */
export const stepsFrom = (civ, unit) => {
  if (!unit) return [];
  const out = [];
  for (let dq = -1; dq <= 1; dq += 1) {
    for (let dr = -1; dr <= 1; dr += 1) {
      const q = unit.q + dq;
      const r = unit.r + dr;
      if (hexDistanceBetween([q, r], [unit.q, unit.r]) !== 1) continue;
      if (mayStandFor(civ, unit, q, r)) out.push([q, r]);
    }
  }
  for (const [q, r] of roadReach(civ, unit.q, unit.r, (a, b) => mayStandFor(civ, unit, a, b))) {
    if (!out.some(([a, b]) => a === q && b === r) && !(q === unit.q && r === unit.r)) out.push([q, r]);
  }
  return out;
};

/** Camps and raiders this unit could attack from where it stands. */
export const foesNear = (civ, unit) => {
  if (!unit) return [];
  const near = (x) => hexDistanceBetween([x.q, x.r], [unit.q, unit.r]) <= 1;
  return [
    ...(civ.menaces || []).filter(near).map((m) => ({ ...m, foe: 'camp' })),
    ...raidersOf(civ).filter(near).map((x) => ({ ...x, foe: 'raider' })),
  ];
};

// ------------------------------------------------------------------ fighting
/**
 * An EXCHANGE, not a verdict. Both sides roll their strength in dice, every
 * face at or above the hit number is a hit, and each hit takes one strength
 * off the other side - simultaneously, so there is no first-strike snowball.
 *
 * This is what killed the zeros in the old system: there is no longer any
 * "you need N hits in a single throw", so no matchup is arithmetically
 * impossible, only expensive. A camp too big for one warband is exactly what
 * a second warband is for.
 *
 * Returns [{ mine, theirs, ours, foe }, rng] - the dice each side rolled and
 * what each side has left.
 */
export const exchange = (civ, unit, foe, rng) => {
  const roll = (n, at, r0) => {
    const faces = [];
    let r1 = r0;
    for (let i = 0; i < n; i += 1) { let f; [f, r1] = nextInt(6, r1); faces.push(f + 1); }
    return [faces, faces.filter((f) => f >= at).length, r1];
  };
  let rng1 = rng;
  let mine; let ourHits;
  [mine, ourHits, rng1] = roll(ourDice(civ, unit), ourHitOn(civ, unit), rng1);
  let theirs; let theirHits;
  [theirs, theirHits, rng1] = roll(foe.strength, BAND.hitOn, rng1);
  return [{
    mine,
    theirs,
    ours: Math.max(0, unit.strength - theirHits),
    foe: Math.max(0, foe.strength - ourHits),
    ourHits,
    theirHits,
  }, rng1];
};

/** The number our dice hit on. Bronze and Iron buy this down. */
export const ourHitOn = (civ, unit = null) => Math.max(2, BAND.hitOn + modsOf(civ).hitOn - (isVeteran(unit) ? 1 : 0));
/** A warband that has won VETERAN_AT fights hits one better. */
export const isVeteran = (unit) => (unit?.wins || 0) >= VETERAN_AT;
/** What a warband is called as the tree arms it: Warband, Spearmen (Bronze), Legion (Iron), Knights (Steel). */
export const warbandTitle = (civ) => (WARBAND_TIERS.find(([tech]) => (civ?.techs || []).includes(tech)) || [null, UNIT_KINDS.warband.name])[1];
export const unitKindName = (civ, unit) => (unit?.kind === 'warband' || !unit?.kind ? warbandTitle(civ) : kindOf(unit).name);
/** The dice our side throws: its strength, plus what Steel and Metalwork add. */
export const ourDice = (civ, unit) => unit.strength + Math.max(0, modsOf(civ).bandDice);

/**
 * What the board says BEFORE you commit. An attrition fight's honest preview
 * is not a win percentage, it is the TRADE - what you expect to deal and
 * what you expect to take. Matt's "a fight is a bet" rule, in the shape this
 * combat actually has.
 */
export const tradePreview = (civ, unit, foe) => {
  const ourP = (7 - ourHitOn(civ, unit)) / 6;
  const theirP = (7 - BAND.hitOn) / 6;
  return {
    deal: Math.round(ourDice(civ, unit) * ourP * 10) / 10,
    take: Math.round(foe.strength * theirP * 10) / 10,
    clears: ourDice(civ, unit) * ourP >= foe.strength,
  };
};

// -------------------------------------------------------------- the camps
/**
 * THE CAMP DOES NOT GET STRONGER, IT GETS BUSIER. Each night it rolls a die
 * and sends a raider on a roll at or under its age in rounds: a fresh camp
 * is one in six, three rounds old is even money, six rounds old sends one
 * every night. Matt, 2026-09-20: "that number increases so that with each
 * subsequent turn they are more likely to spawn somebody."
 *
 * Same escalating dread as the old +1-a-night growth, except the escalation
 * arrives as pieces on the map you can intercept rather than as a number you
 * cannot beat.
 */
export const sendsTonight = (camp, roll, abroad = 0) => (camp.age || 0) > 0 && (camp.age % CAMP.sendEvery) === 0 && abroad < CAMP.cap;

/**
 * Where a raider appears: a random hex AROUND the camp, not in it. Matt:
 * "one camp might end up attacking different villages based on which side of
 * the camp the raider spawned." It is more than flavour - a camp's threat
 * has a DIRECTION you have to read each time, so it cannot be solved once by
 * parking a warband on the obvious side.
 */
export const spawnHexes = (civ, camp) => {
  const out = [];
  for (let dq = -1; dq <= 1; dq += 1) {
    for (let dr = -1; dr <= 1; dr += 1) {
      const q = camp.q + dq;
      const r = camp.r + dr;
      if (hexDistanceBetween([q, r], [camp.q, camp.r]) !== 1) continue;
      if (!canStand(q, r, civ)) continue;
      if ((civ.menaces || []).some((m) => m.q === q && m.r === r)) continue;
      if (raiderAt(civ, q, r)) continue;
      out.push([q, r]);
    }
  }
  return out;
};

/**
 * What a camp of this kind sends. An old camp sends BIGGER raiders: +1
 * strength per RAIDER_GROW.every nights it has stood, up to RAIDER_GROW.max
 * more - so the camp is worth walking to, not just its raiders worth
 * swatting at the town's edge.
 */
export const raiderFor = (camp) => {
  const base = RAIDER_KINDS[camp.kind] || RAIDER_KINDS.camp;
  const grown = Math.max(0, Math.min(RAIDER_GROW.max, Math.floor((camp.age || 0) / RAIDER_GROW.every)));
  return { ...base, strength: base.strength + grown };
};

/**
 * What a raider is walking toward: the nearest village, or the city if there
 * is nothing closer. Straight at it for now - Matt: "we may want a more
 * interesting decision map for what the camp units do, so they aren't just
 * always going in a straight line, but for now let's just go with that."
 */
export const raiderTarget = (civ, raider) => {
  const villages = (civ.villages || []).filter((v) => !v.taken).map((v) => ({ kind: 'village', id: v.id, q: v.q, r: v.r }));
  const city = { kind: 'city', id: 'city', q: 0, r: 0 };
  const all = [...villages, city];
  let best = null;
  all.forEach((t) => {
    const d = hexDistanceBetween([t.q, t.r], [raider.q, raider.r]);
    if (!best || d < best.d) best = { ...t, d };
  });
  return best;
};

/** One step of a raider's walk toward its target, or null if it is there. */
export const raiderStep = (civ, raider) => {
  const target = raiderTarget(civ, raider);
  if (!target || target.d === 0) return null;
  let best = null;
  for (let dq = -1; dq <= 1; dq += 1) {
    for (let dr = -1; dr <= 1; dr += 1) {
      const q = raider.q + dq;
      const r = raider.r + dr;
      if (hexDistanceBetween([q, r], [raider.q, raider.r]) !== 1) continue;
      // Raiders walk on any land, including yours - that is the point of
      // them - but they will not stack with each other or with a camp.
      if (!canStand(q, r, civ) && !isTown(q, r)) continue;
      if (raiderAt(civ, q, r)) continue;
      if ((civ.menaces || []).some((m) => m.q === q && m.r === r)) continue;
      const d = hexDistanceBetween([q, r], [target.q, target.r]);
      if (!best || d < best.d) best = { q, r, d };
    }
  }
  return best;
};

/** A raider entering a hex a unit stands in has to get past it first. */
export const blockedBy = (civ, q, r) => unitAt(civ, q, r) || (civ.forts || []).includes(`${q},${r}`);

// Every unit gets a name (playtest 7: "the war bands are all called the
// same thing... they need a number or maybe even better, a cool, randomly
// generated, but fun name"). Deterministic from the unit's id, so it never
// changes and needs no state.
export const UNIT_NAMES = [
  'Hawks', 'Oakshields', 'Grey Wolves', 'Ravens', 'Ashwalkers', 'Stags', 'Longspears', 'Otters',
  'Red Hands', 'Foxes', 'Stonecrows', 'Boars', 'Hollies', 'Nightjars', 'Ironwoods', 'Adders',
];
export const unitName = (u) => {
  const n = Number(String(u?.id || '').replace(/\D/g, '')) || 0;
  const base = UNIT_NAMES[(n - 1 + UNIT_NAMES.length) % UNIT_NAMES.length];
  const lap = Math.floor((Math.max(n, 1) - 1) / UNIT_NAMES.length);
  return `the ${base}${lap ? ` ${lap + 1}` : ''}`;
};
export const describeUnit = (u, civ = null) => `${unitName(u)} (${isVeteran(u) ? 'veteran ' : ''}${(civ ? unitKindName(civ, u) : kindOf(u).name).toLowerCase()}${u.strength > 1 ? ` of ${u.strength}` : ''})`;

export const seenFrom = (civ, unit) => BAND.sight + modsOf(civ).sight + (kindOf(unit).sight || 0);

/** Every hex the commonwealth holds: the city's, and every village. */
export const heldHexes = (civ) => {
  const out = new Set((civ.city?.hexes || []).map(([q, r]) => hexKey(q, r)));
  (civ.villages || []).forEach((v) => out.add(hexKey(v.q, v.r)));
  return out;
};
